import Dexie, { Table } from 'dexie';

interface ProjectList {
  id?: number;
  name: string;
  path: string;
  language: string;
  testFramework: string;
  automationFramework: string;
  hypProjectId: string;
  createdAt: Date;
  updatedAt: Date;
  localEnv: {
    [key: string]: string;
  };
}

interface YAMLlist {
  id?: number;
  projectId: number;
  name: string;
  path: string;
  jobLabel: string[];
  runson: string;
  mode: string;
  concurrency: number;
  tunnel: boolean;
  selectedQuery?: QueryList;
  selectedTests:
    | {
        groupBy: string;
        selectedTests: {
          [key: string]: [string];
        };
      }
    | null
    | undefined;
  selectedModeIdx?: number;
  selectedDynamicGroupByIdx?: number;
  selectedOrchLevel?: {
    [key: number]: number;
  };
  dynamicTestList?: {
    [key: number]: string[];
  };
  isV2Yaml?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface JobRunsList {
  id?: number;
  projectId: string;
  jobNumber: string;
  createdAt: Date;
  updatedAt: Date;
  message: string | undefined;
  jobId: string | undefined;
  status: string | undefined;
  logPath: string | undefined;
}

interface FileStorage {
  filePath: string;
  content: string;
}

interface QueryList {
  id?: number;
  yamlId: number;
  configName: string;
  query: string;
  createdAt: Date;
  updatedAt: Date;
}

interface DB extends Dexie {
  projectList: Table<ProjectList, string>;
  yamlList: Table<YAMLlist, number>;
  jobRunsList: Table<JobRunsList, number>;
  queryList: Table<QueryList, number>;
  fileStorage: Table<FileStorage, string>;
}

// Store database instances for each user
const userDatabases: { [userId: string]: DB } = {};

// Request persistent storage for IndexedDB
export const requestPersistentStorage = async () => {
  if (
    navigator.storage &&
    navigator.storage.persisted &&
    navigator.storage.persist
  ) {
    const isPersisted = await navigator.storage.persisted();
    if (isPersisted) {
      console.log(':) Storage is already persisted.');
    } else {
      console.log(':( Storage is not persisted. Trying to persist...');
      const granted = await navigator.storage.persist();
      if (granted) {
        console.log(':) We successfully turned the storage to be persisted.');
      } else {
        console.log(':( Failed to make storage persisted');
      }
    }
  } else {
    console.log('StorageManager API is not available in this browser.');
  }
};

// Function to get or create a database instance for a specific user
export const getUserDB = (userId: string): DB => {
  if (userId && !userDatabases[userId]) {
    const db = new Dexie(`hypDB_${userId}`) as DB;

    // Version 2 schema
    db.version(2)
      .stores({
        projectList: '++id, name, path,updatedAt',
        yamlList: '++id, projectId, name, path, updatedAt',
        jobRunsList: '++id, projectId, jobNumber,updatedAt',
        queryList: '++id, yamlId, configName, updatedAt',
        fileStorage: 'filePath', // Add fileStorage to all versions for compatibility
      })
      .upgrade(async (tx) => {
        await tx
          .table('yamlList')
          .toCollection()
          .modify((item: YAMLlist) => {
            item.selectedTests = {
              groupBy: '',
              selectedTests: {},
            };
          });
      });

    // Version 3 schema
    db.version(3)
      .stores({
        projectList: '++id, name, path,updatedAt',
        yamlList: '++id, projectId, name, path, updatedAt',
        jobRunsList: '++id, projectId, jobNumber,updatedAt',
        queryList: '++id, yamlId, configName, updatedAt',
        fileStorage: 'filePath', // Add fileStorage to all versions for compatibility
      })
      .upgrade(async (tx) => {
        await tx
          .table('projectList')
          .toCollection()
          .modify((item: ProjectList) => {
            item.hypProjectId = '';
          });
        await tx
          .table('jobRunsList')
          .toCollection()
          .modify((item: JobRunsList) => {
            item.message = '';
            item.logPath = '';
          });
      });

    // Version 4 schema - fileStorage was officially added here
    db.version(4).stores({
      projectList: '++id, name, path,updatedAt',
      yamlList: '++id, projectId, name, path, updatedAt',
      jobRunsList: '++id, projectId, jobNumber,updatedAt',
      queryList: '++id, yamlId, configName, updatedAt',
      fileStorage: 'filePath',
    });

    requestPersistentStorage();

    userDatabases[userId] = db;
  }

  return userDatabases[userId];
};

export type { ProjectList, YAMLlist, JobRunsList, QueryList, DB, FileStorage };
